/**
 * drawOrderStrata — the DRAWORDER position a direct fill mesh renders at.
 *
 * # The gap this closes
 *
 * `BatchedLayer.bulkLoad` sorts the feed by `(zOrder, id)` and packs it into
 * the line buckets in that order, so DRAWORDER is preserved *inside* a bucket:
 * a later entity's segments are appended after an earlier one's and paint over
 * them. A direct mesh — a wide polyline's band fill, a hatch, a wipeout — is
 * NOT in any bucket. It is a `THREE.Mesh` of its own, and the only ordering
 * primitive it has against the buckets is `renderOrder`.
 *
 * Giving that mesh a single global constant therefore takes it out of
 * DRAWORDER entirely. `WIDE_POLYLINE_FILL_RENDER_ORDER = -0.5` (Codex PR #785
 * round 11, this module's reason to exist) put every band fill BELOW every
 * line bucket, so a line authored EARLIER than the band still painted over the
 * band's supposedly solid interior — the mesh does not write depth, so nothing
 * else could stop it either. Flipping the constant to the other side of the
 * bucket wall is not a fix: it would put the fill over its OWN silhouette,
 * which is the stroke that carries the per-instance selection colour and the
 * minimum on-screen width (`batchedLayer.ts::widePolylineLineVertices`).
 *
 * The four constraints are only satisfiable at once if the buckets stop being
 * ONE wall. So the feed is cut into DRAWORDER STRATA, and both the buckets and
 * the direct fill meshes are ordered by the stratum they fall in:
 *
 *   stratum 0 lines  ── renderOrder 0
 *     band fill in stratum 1 ── strictly between: paints over stratum 0
 *   stratum 1 lines  ── renderOrder 1/2
 *     band fill in stratum 2 ── strictly between
 *   stratum 2 lines  ── renderOrder 2/3 …
 *
 * A band's own silhouette belongs to the stratum the band OPENS, so the fill
 * is always strictly below it ({@link fillStratumRenderOrder} <
 * {@link lineStratumRenderOrder} of the same stratum) — never equal, so
 * three.js's per-frame sort is never asked to break a tie between a band's
 * interior and its own outline.
 *
 * # A boundary is only cut where it changes a pixel
 *
 * A stratum boundary costs draw calls: every bucket key that has entities on
 * both sides of it becomes two buckets, two materials and two selection
 * textures. `batchedLayer.ts` already refuses to put CELTSCALE in the
 * `MeshKey` for exactly this reason, and `scene3d-virtualization` treats the
 * bucket count as a hot-path budget, so a boundary per band would be a
 * regression on a 685k-entity drawing.
 *
 * So a band opens a new stratum ONLY when its own bounds intersect the union
 * of the bounds already assigned to the current stratum — i.e. only when some
 * earlier entity could actually be under its interior. A drawing whose bands
 * do not overlap anything authored before them plans exactly ONE stratum, and
 * one stratum reproduces the previous constants EXACTLY: lines at 0, fills at
 * -0.5. The union is a conservative over-approximation (it can cut a boundary
 * that no pixel needed), never an under-approximation, so correctness never
 * depends on how coarse it is.
 *
 * # The plan must be cut over the WHOLE DRAWING, not over one feed
 *
 * Codex PR #785 round 13, P1. {@link DrawOrderStrata.stratumOf} can only
 * resolve against boundaries that EXIST, and on the cache-direct path the feed
 * `BatchedLayer.bulkLoad` receives is NOT the drawing:
 * `scene/sceneBulkLoadHelpers.ts partitionStubsByLod` drops every stub that
 * measures sub-0.5 px at the fit-to-extents zoom, and `scene/FrameLoops.tsx`
 * Pass 2 hands those ids to `addEntity` later, on zoom-in. A band that was
 * dropped is a band the plan never saw, so no boundary was cut anywhere in its
 * band of DRAWORDER: it relifts into stratum 0, its fill takes -0.5, and an
 * earlier line paints straight through its interior again — the exact defect
 * the strata exist to remove.
 *
 * The relift also arrives with NO USABLE DRAWORDER KEY of its own. The
 * on-demand lift (`scene/SceneBulkLoad.ts`, the `entityResolverRef` closure)
 * runs through `state/spatialBuilder.ts liftRenderableForId` with the pass's
 * BASE adapter context, whose `zOrderIndex` is the constant `0`
 * (`scene/buildAdapterCtxBase.ts`) — so every relifted entity reports
 * `zOrder === 0` no matter where it was authored. A boundary search keyed on
 * `(zOrder, id)` therefore cannot place a relift even when the boundary DOES
 * exist, which is why re-cutting the plan per relift batch would not have been
 * enough on its own.
 *
 * So the plan is cut ONCE over the drawing's full DRAWORDER sequence — every
 * metadata stub, lifted or not ({@link planDrawOrderStrataFrom}, driven by
 * `scene/drawOrderPlanForDrawing.ts`) — and it remembers, BY ID, the stratum
 * each position landed in ({@link DrawOrderStrata.stratumOf} consults that
 * first). An entity then lands in the stratum its AUTHORED position dictates
 * regardless of how it arrived: bulk load, relift, or a relift of a satellite
 * (resolved through `renderParentId`, since a block child has no stub of its
 * own). Planning up front rather than re-planning per batch is also what keeps
 * the stratum a `MeshKey` names STABLE for the life of the load — a boundary
 * inserted after the fact renumbers every stratum above it and leaves every
 * already-packed bucket keyed to a stratum that no longer means what it did.
 *
 * The id map is only as big as it has to be: positions in stratum 0 are NOT
 * stored, and that is also what a position the plan never placed resolves to
 * ({@link DrawOrderUnplannedRule} `"stratum-zero"`). A drawing that cuts ONE
 * stratum stores nothing at all and is byte-identical to the pre-strata
 * renderer.
 *
 * The boundary KEYS are not searchable for such a plan and are not used that
 * way: the stub sequence and the render feed are numbered in different spaces
 * (a feed `zOrder` is the index in the LIFTED list, a stub's is the index in
 * the whole drawing, and a relift's is 0), so a key search across them would
 * answer about a coincidence. The feed-cut plan ({@link planDrawOrderStrata},
 * the legacy branch) is numbered in ONE space and keeps the search, which is
 * what an `addEntity` of an entity created after the load still resolves
 * through.
 *
 * # Where the stack sits
 *
 * HATCH (-2) < WIPEOUT mask (-1) < **band fills and line buckets, in
 * DRAWORDER, within [-0.5, 1)** < arrow decorators / revision clouds (1) <
 * text (2). `lineStratumRenderOrder` is asymptotic to 1 and
 * `fillStratumRenderOrder` never goes below -0.5, so no number of strata can
 * push line work over an arrow head or a band fill under a wipeout — the two
 * ends of the pre-existing stack are preserved by construction rather than by
 * a cap that could be exceeded.
 */

import {
  fillModePaintsSolid,
  polylineDeclaresBandWidth,
} from "./adapters/geom/polylineDrawnBand";
import type { RenderableEntity } from "./types";

/** The `(zOrder, id)` position an entity occupies in DRAWORDER. */
export interface DrawOrderKey {
  readonly zOrder: number;
  readonly id: string;
}

/**
 * The ONE DRAWORDER comparator. `BatchedLayer.bulkLoad` packs the buckets in
 * this order and {@link planDrawOrderStrata} cuts the strata in it, so the two
 * cannot drift: the stratum a bucket's entities land in is decided by the same
 * sequence that decides who paints over whom inside the bucket.
 */
export function compareDrawOrder(a: DrawOrderKey, b: DrawOrderKey): number {
  if (a.zOrder !== b.zOrder) return a.zOrder - b.zOrder;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * `renderOrder` for the LINE buckets of stratum `s`: `1 - 1/(s+1)`.
 *
 * Strictly increasing, `0` for stratum 0 (so a single-stratum drawing keeps
 * the bucket meshes at the default `renderOrder` they have always had) and
 * asymptotic to 1 from below, so an unbounded number of strata still fits
 * under the arrow / cloud layer at 1 without a cap. The gap between adjacent
 * strata shrinks as `1/(s+1)²`; at ~1e7 strata it reaches the double
 * precision of a JS `renderOrder` compare, which is four orders of magnitude
 * past the entity count this renderer targets.
 */
export function lineStratumRenderOrder(stratum: number): number {
  return 1 - 1 / (stratum + 1);
}

/**
 * `renderOrder` for a direct BAND FILL in stratum `s`: strictly above every
 * line bucket of stratum `s - 1` and strictly below the buckets of its own
 * stratum — which is where its own silhouette lives.
 *
 * Stratum 0 has no stratum below it, so the fill takes the old `-0.5`: below
 * the buckets at 0 and above the wipeout mask at -1, exactly the stack the
 * constant encoded.
 */
export function fillStratumRenderOrder(stratum: number): number {
  if (stratum <= 0) return -0.5;
  return (
    (lineStratumRenderOrder(stratum - 1) + lineStratumRenderOrder(stratum)) / 2
  );
}

/**
 * What {@link DrawOrderStrata.stratumOf} is asked about: a DRAWORDER key, plus
 * the satellite attribution that stands in for one.
 *
 * A render-only satellite (`scene/renderSatellites.ts` — a block child, an
 * MLEADER's block content) has no metadata stub and therefore no position of
 * its own in the planned sequence; it rides with the ONE spatial-index entity
 * `renderParentId` names, and paints in that entity's stratum.
 */
export interface DrawOrderSubject extends DrawOrderKey {
  readonly renderParentId?: string;
}

/** An entity's DRAWORDER stratum, resolvable for any entity, planned or not. */
export interface DrawOrderStrata {
  /** Number of strata the plan cut (always ≥ 1). */
  readonly count: number;
  /** The `(zOrder, id)` of the first entity of each stratum after the first. */
  readonly boundaries: readonly DrawOrderKey[];
  /**
   * The stratum `entity` renders in — resolved by IDENTITY first (the plan
   * remembers every position it placed above stratum 0), then through
   * `renderParentId` for a satellite, and only then by a search over the
   * boundary keys.
   *
   * The identity arm is what makes the FrameLoops relift path
   * (`BatchedLayer.addEntity`, `emitReliftDirectMeshes`) correct: a relift
   * carries `zOrder === 0` whatever its authored position
   * (`scene/buildAdapterCtxBase.ts`), so a key search alone would put every
   * relifted entity in stratum 0. See this module's "WHOLE DRAWING" note.
   */
  stratumOf(entity: DrawOrderSubject): number;
}

/** The plan for a feed with nothing to separate: one stratum, old constants. */
export const SINGLE_DRAW_ORDER_STRATUM: DrawOrderStrata = {
  count: 1,
  boundaries: [],
  stratumOf: () => 0,
};

/** Does this renderable paint a solid band interior as a direct mesh? */
export function drawsBandFill(entity: RenderableEntity): boolean {
  return (
    entity.geometry.kind === "polyline" &&
    polylineDeclaresBandWidth(entity.geometry) &&
    fillModePaintsSolid(entity.fillmode)
  );
}

interface Cover {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function intersects(a: Cover, b: RenderableEntity["bounds"]): boolean {
  return (
    a.minX <= b.maxX && b.minX <= a.maxX && a.minY <= b.maxY && b.minY <= a.maxY
  );
}

/** The least a position in DRAWORDER has to say for the planner to place it. */
export interface DrawOrderPlanItem extends DrawOrderKey {
  readonly bounds: RenderableEntity["bounds"];
}

/**
 * What a plan answers for a position it never placed.
 *
 * `"search-boundaries"` — the sequence and the entities that will be asked
 * about share ONE numbering (the legacy feed plan), so the number of boundary
 * keys at or before the entity's `(zOrder, id)` is its stratum.
 *
 * `"stratum-zero"` — they do not (the cache-direct drawing plan: see the
 * module's "WHOLE DRAWING" note), so the only honest answer is the base
 * stratum. Every position the plan DID place above it is in the id map, so
 * this arm is reached only by an entity created after the load — which lands
 * in stratum 0 either way, since it carries no DRAWORDER the plan knows.
 */
export type DrawOrderUnplannedRule = "search-boundaries" | "stratum-zero";

/**
 * Cut `sorted` — a DRAWORDER sequence ALREADY in {@link compareDrawOrder}
 * order — into DRAWORDER strata, asking `opensBandStratum` which of its
 * positions paint a band interior.
 *
 * One O(n) pass, four float compares and a bounds union per position.
 * `opensBandStratum` is consulted ONLY for a position whose bounds already
 * intersect the current stratum's cover — the free geometric test gates the
 * one that may have to resolve an entity — and every position contributes its
 * bounds to the cover whether or not it can open a boundary, because the cover
 * answers "could something be UNDER the next band" and a position that is not
 * DRAWN yet is still authored.
 *
 * `sorted` is the WHOLE DRAWORDER sequence on the cache-direct path
 * (`scene/drawOrderPlanForDrawing.ts`) and the whole feed on the legacy one
 * ({@link planDrawOrderStrata}); either way it is the complete authored order,
 * which is what lets the returned plan answer for an entity that arrives
 * later.
 *
 * @param unplanned what to answer for a position this plan never placed
 *   ({@link DrawOrderUnplannedRule}).
 */
export function planDrawOrderStrataFrom<T extends DrawOrderPlanItem>(
  sorted: readonly T[],
  opensBandStratum: (item: T) => boolean,
  unplanned: DrawOrderUnplannedRule,
): DrawOrderStrata {
  const boundaries: DrawOrderKey[] = [];
  // Only positions ABOVE stratum 0 are remembered — see the module doc's note
  // on why stratum 0 needs no entry to be answered correctly.
  const stratumById = new Map<string, number>();
  let cover: Cover | null = null;
  let stratum = 0;
  for (const entity of sorted) {
    const bounds = entity.bounds;
    if (
      cover !== null &&
      intersects(cover, bounds) &&
      opensBandStratum(entity)
    ) {
      // Something already assigned to this stratum could lie under the band's
      // interior. Open the next stratum so the fill can be ordered above it.
      boundaries.push({ zOrder: entity.zOrder, id: entity.id });
      stratum += 1;
      cover = null;
    }
    if (stratum > 0) stratumById.set(entity.id, stratum);
    if (cover === null) {
      cover = {
        minX: bounds.minX,
        minY: bounds.minY,
        maxX: bounds.maxX,
        maxY: bounds.maxY,
      };
    } else {
      if (bounds.minX < cover.minX) cover.minX = bounds.minX;
      if (bounds.minY < cover.minY) cover.minY = bounds.minY;
      if (bounds.maxX > cover.maxX) cover.maxX = bounds.maxX;
      if (bounds.maxY > cover.maxY) cover.maxY = bounds.maxY;
    }
  }
  if (boundaries.length === 0) return SINGLE_DRAW_ORDER_STRATUM;
  return {
    count: boundaries.length + 1,
    boundaries,
    stratumOf: (entity) => {
      const own = stratumById.get(entity.id);
      if (own !== undefined) return own;
      const parentId = entity.renderParentId;
      if (parentId !== undefined) {
        const parent = stratumById.get(parentId);
        if (parent !== undefined) return parent;
      }
      // Not a placed position: an entity created after the load, or one this
      // plan's numbering cannot speak about at all.
      if (unplanned === "stratum-zero") return 0;
      // Number of boundaries at or before its key, by binary search.
      let lo = 0;
      let hi = boundaries.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (compareDrawOrder(boundaries[mid]!, entity) <= 0) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    },
  };
}

/**
 * {@link planDrawOrderStrataFrom} over a feed of LIFTED renderables, whose
 * band-ness is read straight off the geometry the renderer is about to draw
 * ({@link drawsBandFill}).
 *
 * The feed IS the whole drawing on the legacy (non-cache-direct) branch —
 * nothing is dropped before `bulkLoad` there and no relift resolver is wired —
 * so this is the whole plan for it. The cache-direct branch plans from the
 * drawing instead (`scene/drawOrderPlanForDrawing.ts`) and hands the result to
 * `BatchedLayer.bulkLoad`; see this module's "WHOLE DRAWING" note for why its
 * feed is not enough.
 */
export function planDrawOrderStrata(
  sorted: readonly RenderableEntity[],
): DrawOrderStrata {
  return planDrawOrderStrataFrom(sorted, drawsBandFill, "search-boundaries");
}
